import { useEffect } from 'react';

import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * @file THE PHONE'S TWO NON-MODAL PIECES: the slab that takes it out of your
 * pocket, and the banner that tells you it is ringing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ TWO SEPARATE TARGETS, NEVER ONE BUTTON THAT CHANGES MEANING
 * ─────────────────────────────────────────────────────────────────────────────
 * The obvious build is ONE pill: it opens the phone normally, and when a call is
 * ringing the same pill answers instead. That is one target with two meanings —
 * the exact mis-tap generator `docs/ui.md` bans ("two zones, never two
 * gestures", the rule the ammo slab is built on) — and here the mis-tap is
 * expensive in a way a mis-tapped reload is not: the player either misses a
 * story beat or answers a call they meant to put off, and neither is undoable.
 * The meaning of a control must not depend on state the player has not looked
 * at yet.
 *
 * So: `PhoneButton` always means "open the phone" and `PhoneRing` always means
 * "there is a call, here are the two things you can do about it". They never
 * swap, they never move, and they are in different corners of the screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TOUCH CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * This game targets MOBILE, where there is no `P`. Both pieces are real
 * `<button type="button">`s (keyboard- and screen-reader-reachable); every
 * action goes through the BUS rather than through a method, so the touch path
 * and `Gameplay`'s `P` end in the SAME place and neither can grow its own rules;
 * only the BUTTONS take `pointer-events-auto` (the HUD wrapper is
 * `pointer-events-none` so WASD and click-to-move reach the canvas underneath);
 * every tap target carries `touchAction: 'manipulation'` (no 300 ms
 * double-tap-zoom delay) and `onMouseDown` preventDefault (the `Link`/`ShopRow`
 * idiom that stops the canvas losing keyboard focus).
 *
 * ⚠ NEITHER COMPUTES A PERMISSION. Every button here is always tappable; the
 * model refuses silently. A `disabled` derived in the HUD would be a second gate
 * on one question — the `hurtPlayer(amount, lethal)` family of bug.
 *
 * ⚠ AND THERE IS NO WINDOW-CAPTURE KEY LISTENER IN THIS FILE, on purpose.
 * Neither of these is a modal; `P` belongs to `Gameplay`'s `input.onKey`, and
 * capture listeners fire in REGISTRATION ORDER, which is a live bug this project
 * has already paid for once (`PauseMenu` vs `ShopPanel`, `docs/shops.md`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * engine → ui  'phone:ring'  { waiting, id, name, caller }
 * ui → engine  'phone:request' {}    // on mount: the bus has no replay
 * ui → engine  'phone:show'    {}    // PhoneButton
 * ui → engine  'phone:answer'  {}    // PhoneRing — ANSWER
 * ui → engine  'phone:ignore'  {}    // PhoneRing — IGNORE   ⚠ see below
 * ```
 *
 * ⚠ `'phone:ignore'` WAS THE ONE NAME THIS FILE INVENTED, AND IT IS RECONCILED:
 * `systems/Phone.js` subscribes to it and routes it to `ignore()`. The reasoning
 * is kept because it is why the channel exists at all — IGNORE has to say
 * something to the model (the ring is a COUNT, `waiting`, so dismissing one has
 * to decrement it and turn the call into a missed one for the panel's call-back
 * list) and no other channel means that: `'phone:show'` opens a panel and
 * `'phone:call:end'` answers a call that is not running. It is TOKENLESS, like
 * `'phone:show'` and `'phone:answer'`, because a non-modal HUD banner has no
 * token to carry. ⚠ If the model ever stops subscribing, IGNORE becomes a button
 * that does nothing — which is worse than no button — so `tests/phone.test.mjs`
 * is where that gets caught, not a reader's memory.
 *
 * ⚠ `'phone:request'` ON MOUNT IS NOT OPTIONAL — the same rule
 * `hooks/useMissionList.js` exists to own. `'phone:ring'` publishes ON CHANGE,
 * and the HUD can mount long after the last one (a route re-render, a hot
 * reload, a phone that has been ringing since before the loading screen lifted).
 * Without the ask, a player whose phone rang during the fade would never see the
 * slab appear.
 *
 * Both pieces return `null` until `'phone:ring'` has spoken, so every dev scene
 * and every phone-less level renders these two corners EXACTLY as it does today.
 *
 * @module ui/hud/PhoneHud
 */

/**
 * THE SLAB — bottom-left column, above the ammo bar.
 *
 * ⚠ IT DOES NOT POSITION ITSELF, for the same reason `AmmoBar`, `HealthBar` and
 * `Minimap` do not: that column in `routes/PlayRoute.jsx` owns the layout, and
 * shares ONE width. Four self-positioning slabs in one corner drift apart the
 * moment any of them changes size.
 *
 * The key is PRINTED on the widget so the binding and the pixel cannot drift
 * apart (`docs/ui.md`). The waiting count is a STATE the slab reports, not a
 * second control — answering is `PhoneRing`'s job and only `PhoneRing`'s.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {number} [props.width=220] CSS px — match the minimap.
 */
export function PhoneButton({ engine, width = 220 }) {
  const ring = useEngineBus(engine, 'phone:ring', { throttle: 100 });

  // See the header: ask, because the bus has no replay and this channel fires on
  // change. Declared after the subscription so the listener is installed first —
  // both effects belong to the same commit.
  useEffect(() => {
    engine?.bus?.emit?.('phone:request');
  }, [engine]);

  if (!ring) return null;

  const waiting = Math.max(0, Math.floor(Number(ring.waiting) || 0));

  return (
    <div className="select-none" style={{ width: `${width}px` }}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => engine?.bus?.emit?.('phone:show')}
        aria-label={waiting > 0 ? `Phone — ${waiting} waiting` : 'Phone'}
        style={{ touchAction: 'manipulation' }}
        className={`pointer-events-auto flex w-full items-center justify-between gap-2 rounded-[3px] border bg-void/80 px-2.5 py-1.5 transition ${
          waiting > 0
            ? 'border-gold/70 hover:border-gold'
            : 'border-hairline hover:border-gold/60'
        }`}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <PhoneGlyph className={waiting > 0 ? 'text-gold' : 'text-hud-dim'} />
          <span
            className={`font-hud truncate text-[11px] font-bold uppercase leading-none tracking-[0.22em] ${
              waiting > 0 ? 'text-gold' : 'text-hud-dim'
            }`}
          >
            Phone
          </span>
        </span>

        {waiting > 0 ? (
          // A COUNT, stated. Not a control: the ring banner is where a call is
          // answered, and this must never become a second way to do that.
          <span className="shrink-0 rounded-[2px] border border-gold/60 bg-gold/15 px-1.5 py-[1px] font-display text-[10px] leading-[1.5] tabular-nums text-gold">
            {waiting}
          </span>
        ) : null}
      </button>

      <div className="px-0.5 pt-1 font-hud text-[9px] uppercase leading-none tracking-[0.18em] text-hud-faint">
        P — Phone
      </div>
    </div>
  );
}

/**
 * THE RING — the incoming-call banner, bottom-centre.
 *
 * ⚠ IT DOCKS ABOVE `Prompt.jsx`, which sits at `bottom-16` and stands ~2.1 rem
 * tall; this sits at `bottom-28` (7 rem), clearing its top edge by ~0.9 rem.
 * Both lines can be up at once — a phone can ring while the player is standing
 * in a doorway — and a banner overlapping the interaction prompt would hide the
 * key that gets them out of the street.
 *
 * ⚠ NO z-INDEX, deliberately, exactly like `Prompt`: this belongs UNDER
 * `FadeOverlay` (z-30), so walking through a door takes the banner with the rest
 * of the screen rather than leaving a call floating over a black frame.
 *
 * Visible only while `waiting > 0` — the model owns that number and this keeps
 * no copy of it.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */
export function PhoneRing({ engine }) {
  const ring = useEngineBus(engine, 'phone:ring', { throttle: 100 });

  // Same ask as `PhoneButton`'s, for the same reason. Two subscribers on one
  // read-only channel is free; a shared parent that owned the subscription would
  // be a layout dependency between two corners of the screen that must not have
  // one.
  useEffect(() => {
    engine?.bus?.emit?.('phone:request');
  }, [engine]);

  const waiting = Math.max(0, Math.floor(Number(ring?.waiting) || 0));
  if (!ring || waiting <= 0) return null;

  const who = firstString(ring.caller, ring.name, ring.id) || 'Unknown caller';
  const bus = engine?.bus ?? null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-28 flex select-none justify-center px-4">
      <div className="hud-fade-in flex items-center gap-3 rounded-[3px] border border-gold/70 bg-void/90 px-3.5 py-2 backdrop-blur-sm">
        <PhoneGlyph className="text-gold" />

        <span className="flex min-w-0 flex-col">
          <span className="font-hud text-[10px] font-bold uppercase leading-none tracking-[0.28em] text-hud-faint">
            {waiting > 1 ? `Incoming · ${waiting} waiting` : 'Incoming call'}
          </span>
          <span className="mt-1 truncate font-display text-[15px] uppercase leading-none tracking-[0.06em] text-gold">
            {who}
          </span>
        </span>

        {/* TWO TARGETS. Answer is the gold one because it is the one the player
            came here to press; ignore is quiet, adjacent and its own button —
            never a long-press, never a swipe, never the same pixel. */}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => bus?.emit?.('phone:answer')}
          aria-label={`Answer call from ${who}`}
          style={{ touchAction: 'manipulation' }}
          className="pointer-events-auto shrink-0 rounded-[3px] border border-gold bg-gold/15 px-3 py-1.5 font-display text-[12px] uppercase leading-none tracking-[0.1em] text-gold transition hover:bg-gold/25"
        >
          Answer
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          // Tokenless, like `'phone:show'` and `'phone:answer'` — a non-modal
          // banner has no token to carry. See the file header.
          onClick={() => bus?.emit?.('phone:ignore')}
          aria-label={`Ignore call from ${who}`}
          style={{ touchAction: 'manipulation' }}
          className="pointer-events-auto shrink-0 rounded-[3px] border border-hairline bg-ink/70 px-3 py-1.5 font-display text-[12px] uppercase leading-none tracking-[0.1em] text-hud-dim transition hover:border-siren hover:text-siren"
        >
          Ignore
        </button>
      </div>
    </div>
  );
}

/**
 * A handset, drawn rather than typed: the display face has no glyph for it and a
 * unicode ☎ renders as an emoji on Windows, which is the wrong register for this
 * HUD. `currentColor` so one shape serves both tones.
 * @param {{className?: string}} props
 */
function PhoneGlyph({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true" className={`shrink-0 ${className}`}>
      <path
        d="M6.6 3.5h3l1.4 3.6-2 1.3a11.4 11.4 0 0 0 5.6 5.6l1.3-2 3.6 1.4v3a1.6 1.6 0 0 1-1.8 1.6C10.4 17.6 6.4 13.6 5 5.3A1.6 1.6 0 0 1 6.6 3.5z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * First non-empty string of the arguments. The model may name a caller as
 * `caller`, `name` or only `id`; this prints the most specific it was given and
 * never invents one.
 * @param {...any} vals
 * @returns {string}
 */
function firstString(...vals) {
  for (const v of vals) if (typeof v === 'string' && v) return v;
  return '';
}
